import { EMusicItemType } from '@hts/entities/Card/enums';
import styles from '@hts/entities/Card/styles/InfoContent/AddMusic/MusicList.module.scss';
import { getMusicListState } from '@hts/entities/Card/utils';
import { ICardAudioItemInput, ICardFolderInput } from '@hts/shared/types';
import { HtsTypography } from '@hts/shared/ui';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { memo, useCallback, useMemo, useRef } from 'react';
import { MusicBlock } from './MusicBlock';
import { VirtualItemWrapper } from './VirtualItemWrapper';

interface IMusicListProps {
  cardAudios?: ICardAudioItemInput[];
  cardFolders?: ICardFolderInput[];
  checkedSong: ICardAudioItemInput | null;
  alwaysCheckedOne: boolean;
  chooseSong: (song: ICardAudioItemInput | null) => void;
}

const ITEM_HEIGHT = 57;
const FOLDER_DIVIDER_HEIGHT = 20;
const GAP_BETWEEN_AUDIO = 5;
const GAP_BETWEEN_FOLDER = 12;
const GAP_BEFORE_FOLDER = 14;

export const MusicList = memo(
  ({ cardAudios, cardFolders, checkedSong, alwaysCheckedOne, chooseSong }: IMusicListProps) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const musicList = useMemo(
      () => getMusicListState(cardAudios, cardFolders),
      [cardAudios, cardFolders]
    );

    const getItemSize = useCallback(
      (index: number) => {
        const item = musicList[index];
        const nextItem = musicList[index + 1];
        const isLastItem = index === musicList.length - 1;

        let gap = 0;

        if (!isLastItem) {
          if (item.type === EMusicItemType.folder) {
            gap = GAP_BETWEEN_FOLDER;
          } else if (nextItem.type === EMusicItemType.folder) {
            gap = GAP_BEFORE_FOLDER;
          } else {
            gap = GAP_BETWEEN_AUDIO;
          }
        }

        const baseHeight =
          item.type === EMusicItemType.folder ? FOLDER_DIVIDER_HEIGHT : ITEM_HEIGHT;

        return baseHeight + gap;
      },
      [musicList]
    );

    const rowVirtualizer = useVirtualizer({
      count: musicList.length,
      getScrollElement: () => parentRef.current,
      estimateSize: getItemSize,
      getItemKey: (index) => musicList[index].id,
      overscan: 5,
    });

    const renderVirualItem = useCallback(
      (virtualRow: VirtualItem) => {
        const item = musicList[virtualRow.index];

        if (item.type === EMusicItemType.folder) {
          return (
            <VirtualItemWrapper id={item.id} virtualRow={virtualRow}>
              <div className={styles.categoryDividerContainer}>
                <HtsTypography fontWeight="600" style={{ wordBreak: 'break-word' }}>
                  {item.name}
                </HtsTypography>
                <hr />
              </div>
            </VirtualItemWrapper>
          );
        }

        return (
          <VirtualItemWrapper id={item.id} virtualRow={virtualRow}>
            <MusicBlock
              item={item}
              checkedSong={checkedSong}
              alwaysCheckedOne={alwaysCheckedOne}
              chooseSong={chooseSong}
            />
          </VirtualItemWrapper>
        );
      },
      [alwaysCheckedOne, checkedSong, musicList, chooseSong]
    );

    return (
      <div ref={parentRef} className={styles.container}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map(renderVirualItem)}
        </div>
      </div>
    );
  }
);
